import { type ActionEvent, Controller } from "@hotwired/stimulus"

import { renderTurboStream } from "@/helpers/turbo"
import { debounceAsync, hasFocusedElement, setInputValueWithEvents } from "@/helpers/utils"

interface Location {
  locationType: string
  maxDistance: string
  name: string
  slug: string
}

export default class extends Controller {
  static targets = [
    "defaultResultsList",
    "firstResult",
    "hiddenInputLocationType",
    "hiddenInputMaxDistance",
    "hiddenInputSlug",
    "input",
    "turboStreamResultsList"
  ]
  declare readonly defaultResultsListTarget: HTMLOListElement
  declare readonly firstResultTarget: HTMLElement
  declare readonly hiddenInputLocationTypeTarget: HTMLInputElement
  declare readonly hiddenInputMaxDistanceTarget: HTMLInputElement
  declare readonly hiddenInputSlugTarget: HTMLInputElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly turboStreamResultsListTarget: HTMLOListElement

  static values = { endpoint: String, resultsId: String, selectedLocation: Object }
  declare readonly endpointValue: string
  declare readonly resultsIdValue: string
  declare selectedLocationValue: Location

  private lookupAbort = new window.AbortController()

  initialize(): void {
    this.lookup = debounceAsync(this.lookup.bind(this), 250)
  }

  connect(): void {
    // Input disabled by default until controller loaded.
    this.inputTarget.disabled = false
  }

  selectedLocationValueChanged(): void {
    // Set value with `change` only, because `input` would trigger another lookup fetch.
    setInputValueWithEvents(this.inputTarget, this.selectedLocationValue.name, ["change"])

    this.hiddenInputLocationTypeTarget.value = this.selectedLocationValue.locationType
    this.hiddenInputMaxDistanceTarget.value = this.selectedLocationValue.maxDistance
    this.hiddenInputSlugTarget.value = this.selectedLocationValue.slug

    // Only send max distance when it has a value.
    this.hiddenInputMaxDistanceTarget.disabled = !this.selectedLocationValue.maxDistance
  }

  async lookup({ target }: ActionEvent): Promise<void> {
    const input = target as HTMLInputElement
    const query = input.value.trim()

    this.resetSelectedLocation(input.value)

    if (query) {
      await this.renderResults(query)
      this.defaultResultsListTarget.hidden = true
    } else {
      this.turboStreamResultsListTarget.innerHTML = ""
      this.defaultResultsListTarget.hidden = false
    }
  }

  onFocus(): void {
    this.inputTarget.select()
  }

  selectFirstResultIfBlurred({ relatedTarget }: FocusEvent): void {
    const isLocationSelected = this.hiddenInputSlugTarget.value
    const isInputBlank = !this.inputTarget.value
    const hasVisibleResults = !this.firstResultTarget.parentElement!.hidden

    // Do nothing if element still has focus, location already selected, or input blank (because
    // empty location is valid).
    if (hasFocusedElement(this.element, relatedTarget) || isLocationSelected || isInputBlank) {
      return
    }

    // Select first result if there are visible results to select from.
    if (hasVisibleResults) {
      this.firstResultTarget.click()
    } else {
      this.turboStreamResultsListTarget.innerHTML = ""
      this.defaultResultsListTarget.hidden = false
      this.resetSelectedLocation("")
    }
  }

  selectResult({ params: { result } }: ActionEvent): void {
    this.selectedLocationValue = result
  }

  private async renderResults(query: string): Promise<void> {
    const url = new URL(this.endpointValue, window.location.origin)
    const params = new URLSearchParams({
      q: query,
      results_id: this.resultsIdValue
    })
    url.search = params.toString()

    // Abort any previous query and reset controller.
    this.lookupAbort.abort()
    this.lookupAbort = new window.AbortController()

    await renderTurboStream(url.toString(), { signal: this.lookupAbort.signal })
  }

  private resetSelectedLocation(name: string): void {
    this.selectedLocationValue = {
      locationType: "",
      maxDistance: "",
      name,
      slug: ""
    }
  }
}
